/**
 * The minimal attendee shape this map needs — an email and an optional resolved
 * name. Structural so it accepts both `AttendeeSuggestion` and `AttendeeView`
 * without coupling to either module.
 */
interface NamedAttendee {
  email: string;
  name?: string;
}

/** The email's local-part (before `@`), trimmed; null if not a usable email. */
function localPart(email: string): string | null {
  const at = email.indexOf('@');
  if (at <= 0) return null;
  return email.slice(0, at).trim() || null;
}

/**
 * Build the `email → display name` map that `resolveSpeakerName` uses to render
 * an assigned transcript speaker.
 *
 * EVERY current attendee gets an entry — enriched/named ones by their resolved
 * name, and NON-enriched ones (calendar attendees with only an email, marked
 * "Couldn't enrich") by the email's local-part. Without the fallback, a
 * non-enriched attendee had no map entry, so after assigning them to a speaker
 * `resolveSpeakerName` missed and reverted to "Speaker N" — the assignment
 * saved server-side but looked like it did nothing.
 *
 * Keys are lowercased emails. An attendee NOT in the list still has no entry, so
 * the "revert to the raw speaker when an attendee is removed" behavior is
 * preserved.
 */
export function buildAttendeeNameMap(
  attendees: NamedAttendee[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const a of attendees) {
    const name = a.name?.trim() || localPart(a.email) || a.email;
    map[a.email.toLowerCase()] = name;
  }
  return map;
}
